import { BookingEvent } from '@deltager/organizers/admin/shared/models/booking-event.model';
import {
  BookingEventSetupStatus,
} from '@deltager/organizers/admin/modules/booking-event-setup/store/booking-event-setup.state';
import { SaveChangesSource } from '@deltager/organizers/admin/redux/root.state';
import { Action } from '@ngrx/store';
import { Operation } from 'fast-json-patch/module/core';
import {
  BookingEventSetupWizardStep,
} from '@deltager/organizers/admin/modules/booking-event-setup/pages/main/components/wizard/booking-event-setup-wizard-step.enum';

export enum BookingEventActionTypes {
  BookingEventModify = '[Booking event setup] Modify',
  PublishBookingEventRequest = '[Booking event setup] Publish request',
  PublishBookingEventSuccess = '[Booking event setup] Publish success',
  ConstructBookingEventRequest = '[Booking event setup] Construct request',
  ConstructBookingEventSuccess = '[Booking event setup] Construct success',
  BookingEventStatusChange = '[Booking event setup] Status change',
  CreateBookingEventRequest = '[Booking event setup] Create request',
  CreateBookingEventSuccess = '[Booking event setup] Create success',
  LoadBookingEventRequest = '[Booking event setup] Load request',
  LoadBookingEventSuccess = '[Booking event setup] Load success',
  UpdateBookingEventRequest = '[Booking event setup] Update request',
  UpdateBookingEventSuccess = '[Booking event setup] Update success',
  GeneralUpdateBookingEventError = '[Booking event setup] Error caught while modifying or creating booking',
  AdjustDocumentProperties = '[Booking event setup] Adjust document properties',
  UpdateAutomaticPublish = '[Booking event setup] Update automatic publish',
  ChangeBookingEventStep = '[Booking event setup] Change booking setup step',
  ClearBookingEventStore = '[Booking event setup] Clear booking event store',
}

interface BookingEventPayload {
  bookingEvent: BookingEvent;
  preferCachedVersion?: boolean;
}

// Modify
export class BookingEventModifyAction implements Action {
  readonly type = BookingEventActionTypes.BookingEventModify;

  constructor(public payload: BookingEventPayload) {
  }
}

// Construct booking event
export class ConstructBookingEventRequestAction implements Action {
  readonly type = BookingEventActionTypes.ConstructBookingEventRequest;

  constructor(public payload: { wizardId: number; preferCached: boolean }) {
  }
}

export class ConstructBookingEventSuccessAction implements Action {
  readonly type = BookingEventActionTypes.ConstructBookingEventSuccess;

  constructor(public payload: BookingEventPayload) {
  }
}

// Status change
export class BookingEventStatusChangeAction implements Action {
  readonly type = BookingEventActionTypes.BookingEventStatusChange;

  constructor(public payload: { bookingEventStatus: BookingEventSetupStatus }) {
  }
}

// Create booking event
export class CreateBookingEventRequestAction implements Action {
  readonly type = BookingEventActionTypes.CreateBookingEventRequest;

  constructor(public payload: { bookingEvent: BookingEvent; notificationMessage: string; wizardId?: number }) {
  }
}

export class CreateBookingEventSuccessAction implements Action {
  readonly type = BookingEventActionTypes.CreateBookingEventSuccess;

  constructor(public payload: BookingEventPayload) {
  }
}

// Update booking event
export class UpdateBookingEventRequestAction implements Action {
  readonly type = BookingEventActionTypes.UpdateBookingEventRequest;

  constructor(
    public payload: {
      bookingEventId: number;
      updateOperations: Operation[];
      draftMode?: boolean;
      notificationMessage?: string;
      source?: SaveChangesSource;
    },
  ) {
  }
}

export class UpdateBookingEventSuccessAction implements Action {
  readonly type = BookingEventActionTypes.UpdateBookingEventSuccess;

  constructor(public payload: BookingEventPayload) {
  }
}

export class GeneralUpdateBookingEventErrorAction implements Action {
  readonly type = BookingEventActionTypes.GeneralUpdateBookingEventError;
}

// Load booking event
export class LoadBookingEventRequestAction implements Action {
  readonly type = BookingEventActionTypes.LoadBookingEventRequest;

  constructor(public payload: { bookingEventId: number }) {
  }
}

export class LoadBookingEventSuccessAction implements Action {
  readonly type = BookingEventActionTypes.LoadBookingEventSuccess;

  constructor(public payload: BookingEventPayload) {
  }
}

// Publish booking event
export class PublishBookingEventRequestAction implements Action {
  readonly type = BookingEventActionTypes.PublishBookingEventRequest;

  constructor(
    public payload: { bookingEventId: number; updateOperations: Operation[]; notificationMessage?: string },
  ) {
  }
}

export class PublishBookingEventSuccessAction implements Action {
  readonly type = BookingEventActionTypes.PublishBookingEventSuccess;

  constructor(public payload: BookingEventPayload) {
  }
}

// Adjust document properties
export class AdjustDocumentPropertiesAction implements Action {
  readonly type = BookingEventActionTypes.AdjustDocumentProperties;

  constructor(public payload: { documentUrls?: string[] }) {
  }
}

export class UpdateAutomaticPublishAction implements Action {
  readonly type = BookingEventActionTypes.UpdateAutomaticPublish;

  constructor(public automaticPublish: boolean) {
  }
}

// Change booking event step
export class ChangeBookingStepAction implements Action {
  readonly type = BookingEventActionTypes.ChangeBookingEventStep;

  constructor(public payload: { step: BookingEventSetupWizardStep }) {
  }
}

// Clear booking store
export class ClearBookingEventStoreAction implements Action {
  readonly type = BookingEventActionTypes.ClearBookingEventStore;
}
